import type { CSSProperties } from '@linaria/core';
import { cx } from '@linaria/core';
import { SyntheticEvent, memo, useCallback, useRef, useState } from 'react';
import { openMenuDialog } from '../../../../actions/menu';
import { goToNotes } from '../../../../actions/nav';
import { SettingsType, VideoType } from '../../../../constants/enums';
import { locales } from '../../../../locales/en';
import { getAnimationsEnabledfromUeopt } from '../../../../mfcLogic/settings';
import { TransitionTypes } from '../../../../state';
import { openRoom, pinRoom, setLoungeSurrogate } from '../../../../state/state.rooms';
import { isTouchDevice } from '../../../../utilities/browser';
import { motionTransition } from '../../../../utilities/transitions';
import ButtonModelMenu from '../../../Buttons/ButtonModelMenu';
import { useDragHelper } from '../../../Hooks/useDragHelper';
import { useGridCenteredIndexAppState } from '../../../Hooks/useGridCenteredIndexAppState';
import { useFloatingPreviewSelector } from '../../../Hooks/useModal';
import { useIsRoomPinned } from '../../../Hooks/useRoom';
import { useSettingSelector } from '../../../Hooks/useSettings';
import { useUserSelector } from '../../../Hooks/useUser';
import { useVideoPlaying } from '../../../Hooks/useVideo';
import Calendar from '../../../Icons/Calendar';
import MapMarker from '../../../Icons/MapMarker';
import RoomIcon from '../../../Icons/Room';
import IconViewers from '../../../Icons/Viewers';
import BadgeAvatar from '../../../common/BadgeAvatar';
import LiveSnapStream from '../../LiveSnapStream';
import BookmarkBadge from '../Badges/Bookmark';
import FriendBadge from '../Badges/Friend';
import NewAndMissMFCBadges from '../Badges/NewAndMissMFC';
import PopularBadge from '../Badges/Popular';
import PriorMissMFCBadge from '../Badges/PriorMissMFC';
import RecommendationBadge from '../Badges/Recommendation';
import TrendingBadge from '../Badges/Trending';
import GroupShowBanner from '../Banners/GroupShow';
import MissMFCBanner from '../Banners/MissMFC';
import NewModelBanner from '../Banners/NewModel';
import PopularBanner from '../Banners/Popular';
import TrendingBanner from '../Banners/Trending';
import NotesButton from '../Buttons/NotesButton';
import PinButton from '../Buttons/PinButton';
import Container from '../Container';
import LargeFloatingPreview from '../LargeFloatingPreview';
import ModelName from '../ModelName';
import { VideoCover } from '../VideoCover';
import { GetStylesConfig, getAnonymousMissMFCRank, isAnonymousMissMFC, resolveDisplayHighlight } from '../helpers';
import "./Detailed.linaria.module.css!=!./Detailed?./Detailed.linaria.module.css?css=H4sIAAAAAAAAA3VW246jOBB9369oaTXSjLS0MBBIsx9jGWygwNjENreg/fdVuDiGnnmJcoo69qGufBLT4hIwuiM/TvKlJaoEkQ5E/fS8Dfz697+/Pl9uZMJAu6KMzmgpOJu8Uskx9Q/X/I5RmLCiDi5wKaQwnoYn2++w2F5zqzEEo4miJZOKMuUpQqHXadxNh0tYYCMa1oS7y34WJzNTHoeyMt724K1dYZR1kWzyC9zUU1AsNyBFmkvet+KgfWmMzDTCwBcKuuNkTl/+x2MWYCT1QwbDUrHXtSny/R+OSlS0qrvRQybqpg8tOdCPTXAFZbXK/efDKCJ0RxQTxo0EiuYgmftLKG7vUEQ3NykWHYRMGiNbT60x2dk2S1F44oa/53JWfKcmPg4KDqG/ZHLydEWoHE9ZkILtZvs6bYOroG3reOmkhjXainFiYGCHS6Mxug2PcVSLHJgquBzTCihlNiOkxsgvBgG3pQBumEqpkt1+1U9XAeRSHBKshgqjKpZ1/zyjhXAohQeGtTrNmTBMHYzygecq8rPijPZO2YOUBu+MVD5GcxRJXlzgQVmTkUZvRq7xXYQoyM5o65VxK6xMcmqDz3EfJwXSSy65VOnfRVE4cgNWPG7mjC5y3f7ebTZE4wOPWfkF5fL0QFA2pQGKkugexlHipFIbMd7v71SSTEveG5vK4nGU06uA3pVjmsNuZPc2l0+MSNvG5f0CD+XrKSfdL4tVHcO7etdX9N18fKHi+Ugu8JyP09GrySkapvuw7E/gVDLrEGHCZkjYXtoOt2ICitFY+3UmL3DJSN6USvaCeltWN0GOeR0RvM/dEZH4GNVllg3dnxrRVIp9a8U4dzvfoqPzjez+MDJiciKS3xB/Oy/IhJssEcPzjJzdgewVI0bsFj2lXryRZQ28hvnkSQVMmHRgykBOuDOh8xo15d0OaIdkSzrCX3UHelhGqaiXKUaadP31CLdHFTNGECLC3hdzEMzLOWm7I6bWYGNZhhjRe8WfZ7SsrvtacMibxbJfK4Q2tweQ5eR7ccspRm3ol4/pAveFambOUjCEQ25naYRRB/M0NovsSA5mTv3Pm33Z3C1qi5a61waK2culMK94Xyu7dmn1uRc048U3wjDbHTkCNdVpReoHRjzQQIfFsMl4dugzzqHToO0pA0aPrzwvn8tYgWGe7kjOUiFHRTpnhrij+nkZ1esEcQb1MGP17I0KdmFb4Nf/Nu5cuvVu0dIRSkGU3mmGcXZyZlfn62TiDUY8yUKiLtAyVsnhWzLnZwb/xtjmjUMpAZdZcyP6+LbzP5zVEyc4a5gfNk4Hf/tMoYqMTHkUBnC/qfgdR30GmTgu3yO4o19OIcoymUzr1GHsFqK7kt/wWyme9zKXbrItOmVmS/b/3Ibr8+QKAAA="
const stylesDescriptionAvatarClass = "atm_gi_181067c atm_ax_idpfg4";
const stylesNewAndMissMFCBadges = "atm_c8_137efj2";
const stylesDetailedRoot = "atm_5j_i2wt44 atm_3f_tnkek3 atm_ar_1bp4okc atm_9s_1txwivl atm_e2_1osqo2v";
const stylesDetailedRootHighlight = "atm_3f_1fmrp5d";
const detailedContainerStyles = "atm_5j_14y27yu atm_45_idpfg4 atm_43_idpfg4 atm_70_2fli30 atm_mk_h2mmj6 atm_ks_15vqwwr";
const stylesViewerContainer = "atm_aj_10fvni5 atm_h_1h6ojuz atm_gq_yh40bf atm_h0_1y44olf atm_cs_8n312b atm_9s_1txwivl atm_7l_u67f1s";
const badgeContainerStyles = "atm_gq_2efq5t atm_wq_wbg9ig atm_ar_1bp4okc atm_h_1h6ojuz atm_mk_stnw88 atm_9s_1txwivl";
const badgeCornerTopLeft = "atm_fq_idpfg4 atm_tk_idpfg4";
const badgeCornerBottomLeft = "atm_gz_1amm6g8 atm_6i_idpfg4 atm_fq_idpfg4";
const badgeCornerBottomRight = "atm_h0_191fzq7 atm_h_esu3gu atm_6i_idpfg4 atm_n3_idpfg4";
const badgeCornerTopRight = "atm_n3_idpfg4 atm_tk_idpfg4";
const bannerContainerStyles = "atm_5j_i2wt44 atm_2d_1wj0jbo atm_ar_1bp4okc atm_70_1jgbbvp atm_6c_idpfg4 atm_6a_idpfg4 atm_mk_h2mmj6 atm_ks_15vqwwr atm_9s_1txwivl atm_ax_kb7nvz";
const blurbContainerStyles = "atm_6w_1e54zos atm_9s_cj1kg8 atm_w4_9jpisv atm_ks_15vqwwr atm_ax_kb7nvz atm_fy_1i31aes atm_g3_1d8hlz atm_c8_137efj2 atm_e2_1dk5qia";
const blurbStyles = "atm_cd_1m30gqx atm_k4_1piyxwk";
const locationAgeContainer = "atm_fc_esu3gu atm_j_esu3gu atm_h_1h6ojuz atm_9s_1txwivl atm_vy_1osqo2v";
const countryStyles = "atm_sq_1l2sidv atm_vv_1q9ccgz atm_ks_15vqwwr atm_h0_1y44olf";
const calendarStyles = "atm_h0_1y44olf atm_gz_yh40bf";
const stylesPriorMissMFCBadgeClass = "atm_e2_1dk5qia atm_vy_rzutr2 atm_gz_yh40bf atm_9s_1txwivl";
const stylesHeightAndFontSizeAndPaddingDefault = "atm_c8_137efj2 atm_e2_1dk5qia atm_lo_idpfg4 atm_le_idpfg4 atm_lk_1l7b3ar atm_ll_1l7b3ar";
const stylesHeightAndFontSize = "atm_c8_137efj2 atm_e2_1dk5qia";
const stylesMargin = "atm_gi_181067c";
const stylesDivider = "atm_e2_1dk5qia atm_gi_gbk5as";
const stylesDividerBorder = "atm_67_bke03k";
const stylesBadgeAvatar = "atm_70_2fli30";
const stylesViewerCounter = "atm_c8_137efj2 atm_gz_yh40bf";
const stylesBadgeAvatarContainer = "atm_l8_4ubibn atm_9s_1txwivl";
const stylesBlurbOrTopic = "atm_k4_og7xtm";
const stylesDisplayBadges = "atm_9s_1txwivl";
const stylesModelName = "atm_fc_1h6ojuz atm_h_esu3gu atm_lo_idpfg4";
const stylesModelNamePaddinTop = "atm_lo_yh40bf";
interface DetailedProps {
  activeRoom?: number;
  columnWidth: number;
  disableMenuNextPrev?: boolean;
  displayBookmarkBadge?: boolean;
  displayBookmarkHighlight?: boolean;
  displayFriendBadge?: boolean;
  displayFriendHighlight?: boolean;
  displayGroupShowBanner?: boolean;
  displayHeight: number;
  displayMissMFCBadge?: boolean;
  displayMissMFCBanner?: boolean;
  displayNewModelBadge?: boolean;
  displayNewModelBanner?: boolean;
  displayNoteIconButton?: boolean;
  displayPopularBadge?: boolean;
  displayPopularBanner?: boolean;
  displayPopularHighlight?: boolean;
  displayRecentlyVisitedBadge?: boolean;
  displayRecentlyVisitedHighlight?: boolean;
  displayRecommendationBadge?: boolean;
  displayRecommendationHighlight?: boolean;
  displayTrendingBadge?: boolean;
  displayTrendingBanner?: boolean;
  displayTrendingHighlight?: boolean;
  enableCenteredIndexTracking?: boolean;
  enableLargePreview?: boolean;
  enablePin?: boolean;
  enablePinToTopSymbols?: boolean;
  getStyle: (key: string, config?: GetStylesConfig) => any;
  id: number;
  index: number;
  loungeSlug?: string;
  name: string;
  passiveMode?: boolean;
  singlePin?: boolean;
  pinLimitReached?: boolean;
  slug: string;
}
const Detailed = memo((props: DetailedProps) => {
  const {
    enableCenteredIndexTracking,
    displayRecentlyVisitedHighlight,
    displayRecommendationBadge,
    displayRecommendationHighlight,
    displayTrendingHighlight,
    displayBookmarkHighlight,
    displayPopularHighlight,
    displayFriendHighlight,
    displayGroupShowBanner,
    displayTrendingBanner,
    displayNewModelBanner,
    displayPopularBanner,
    displayMissMFCBanner,
    displayRecentlyVisitedBadge,
    displayBookmarkBadge,
    displayTrendingBadge,
    displayNewModelBadge,
    displayPopularBadge,
    displayMissMFCBadge,
    displayFriendBadge,
    displayNoteIconButton,
    enablePinToTopSymbols,
    disableMenuNextPrev,
    enableLargePreview,
    pinLimitReached,
    displayHeight,
    columnWidth,
    passiveMode,
    activeRoom,
    loungeSlug,
    singlePin,
    enablePin,
    getStyle,
    index,
    slug,
    name,
    id
  } = props;
  const snapRef = useRef<HTMLImageElement>(undefined);
  const {
    dragStart,
    dragMove,
    dragEnd
  } = useDragHelper(id, snapRef);
  const [sort] = useSettingSelector(SettingsType.DEFAULT, 'sort');
  const [showVideoPreview, setVideoPreview] = useState(false);
  const [animatePreviews] = useSettingSelector(SettingsType.DEFAULT, 'animatePreviews');
  const staticImage = !getAnimationsEnabledfromUeopt(animatePreviews);
  const isPinnedToTopRecentlyVisited = useUserSelector(id, 'isPinnedToTopRecentlyVisited');
  const isPinnedToTopTrending = useUserSelector(id, 'isPinnedToTopTrending');
  const isPinnedToTopPopular = useUserSelector(id, 'isPinnedToTopPopular');
  const isPinnedToTopMissMFC = useUserSelector(id, 'isPinnedToTopMissMFC');
  const isBanned = useUserSelector(id, 'banned');
  const isGroupShowPending = useUserSelector(id, 'isGroupShowPending');
  const priorMissMfcRank = useUserSelector(id, 'priorMissMfcRank');
  const groupShowRoomCount = useUserSelector(id, 'groupShowRoomCount');
  const isBroadcasting = useUserSelector(id, 'isBroadcasting');
  const isTrendingNew = useUserSelector(id, 'isTrendingNew');
  const isRecommended = useUserSelector(id, 'isPinnedToTopModelYouMayLike');
  const isTruePrivate = useUserSelector(id, 'isTruePrivate');
  const isPrivateShow = useUserSelector(id, 'isPrivateShow');
  const isPublicShow = useUserSelector(id, 'isPublicShow');
  const trendingDiff = useUserSelector(id, 'trendingDiff');
  const missMFCPrize = useUserSelector(id, 'missMFCPrize');
  const isAnonymous = isAnonymousMissMFC(id);
  const missMFCRankFromSelector = useUserSelector(id, 'missMFCRank');
  const missMFCRank = isAnonymous ? getAnonymousMissMFCRank(slug) : missMFCRankFromSelector;
  const isMissMFCFromSelector = useUserSelector(id, 'isMissMFC');
  const isMissMFC = isAnonymous ? true : isMissMFCFromSelector;
  const isGroupShow = useUserSelector(id, 'isGroupShow');
  const isBookmark = useUserSelector(id, 'isBookmark');
  const isNewModel = useUserSelector(id, 'isNewModel');
  const isClubShow = useUserSelector(id, 'isClubShow');
  const isPopular = useUserSelector(id, 'isPopular');
  const isOffline = useUserSelector(id, 'isOffline');
  const isNGVideo = useUserSelector(id, 'isNGVideo');
  const isLounge = useUserSelector(id, 'isLounge');
  const isKicked = useUserSelector(id, 'isKicked');
  const isFriend = useUserSelector(id, 'isFriend');
  const creation = useUserSelector(id, 'creation');
  const country = useUserSelector(id, 'country');
  const viewers = useUserSelector(id, 'viewers');
  const channel = useUserSelector(id, 'channel');
  const isModel = useUserSelector(id, 'isModel');
  const hasNote = useUserSelector(id, 'hasNote');
  const server = useUserSelector(id, 'server');
  const isAway = useUserSelector(id, 'isAway');
  const phase = useUserSelector(id, 'phase');
  const color = useUserSelector(id, 'color');
  const topic = useUserSelector(id, 'topic');
  const blurb = useUserSelector(id, 'blurb');
  const age = useUserSelector(id, 'age');
  const isRoomPinned = useIsRoomPinned(id);
  const isPreviewPinned = useFloatingPreviewSelector(id, 'close');
  const inHiddenShow = isTruePrivate || isPrivateShow || isGroupShow || isClubShow;
  const noShowType = isOffline || inHiddenShow || isAway;
  const isViewable = !noShowType && !isBanned && !isKicked;
  const showSinglePin = singlePin && isViewable;
  const stylesName = getStyle('name');
  const stylesDescription = getStyle('description');
  // const stylesDescriptionPX = getStyle('description', { px: true })
  // const stylesBannerPX = getStyle('banner', { px: true })
  const stylesInfo = getStyle('info');
  // const stylesInfoPX = getStyle('info', { px: true })

  const stylesDescriptionAvatar = stylesDescription('avatar');
  const stylesBanner = getStyle('banner');
  const stylesBannerPopular = stylesBanner('popular');
  const stylesBannerPopularViewers = stylesBannerPopular('viewers');
  const stylesBannerTrending = stylesBanner('trending');
  const stylesBannerTrendingDiff = stylesBannerTrending('diff');
  const stylesBannerNew = stylesBanner('new');
  const stylesBannerNewTime = stylesBannerNew('time');
  const stylesBannerMissMFC = stylesBanner('missMFC');
  const stylesBannerMissMFCRank = stylesBannerMissMFC('rank');
  const stylesUtilities = getStyle('utilities');
  const stylesBadge = getStyle('badge');
  const stylesBadgeStatus = stylesBadge('status');
  const stylesBadgeNewMissMFC = stylesBadge('newMissMFC');
  const stylesPriorMissMFCBadge = stylesBadge('priorMissMFC');
  const stylesViewers = getStyle('viewers');
  const stylesMaxWidth = getStyle('column')('maxWidth');
  const isGridCenteredOnIndex = useGridCenteredIndexAppState(index, enableCenteredIndexTracking);
  const isVideoAlreadyPlaying = useVideoPlaying(id);
  const shouldForceVideoPlay = isGridCenteredOnIndex && !isVideoAlreadyPlaying;

  // if (!id) {
  //     return <AvatarSkeleton width={width} height={height} />
  // }

  //
  // For testing purposes:
  //
  // const candisplayFriendBadge = true
  // const canDisplayBookmarkBadge = true
  // const canDisplayTrendingBadge = true
  // const canDisplayPopularBadge = true
  // const canDisplayNewModelBadge = true
  // const canDisplayMissMFCBadge = true
  // const canDisplayPriorMissMFCBadge = true
  // const canDisplayRecentlyVisitedBadge = true
  // const canDisplayNotesButton = true
  // const canEnablePinToTopSymbols = false
  // const canDisplayLargePreviewButton = false
  // const canDisplayPinButton = true

  //
  // Symbols
  //
  const canEnablePinToTopSymbols = enablePinToTopSymbols;

  //
  // Buttons
  //
  const canDisplayNotesButton = hasNote && displayNoteIconButton;
  const canDisplayLargePreviewButton = enableLargePreview && !isAnonymous && isPublicShow;
  const canDisplayPinButton = (enablePin && !pinLimitReached || isRoomPinned || showSinglePin) && !isAnonymous && isViewable;

  //
  // Banners
  //
  const dicanDisplayGroupShowBanner = displayGroupShowBanner && (isGroupShow || isGroupShowPending || groupShowRoomCount);
  const canDisplayTrendingBanner = displayTrendingBanner && isTrendingNew && trendingDiff;
  const canDisplayNewModelBanner = displayNewModelBanner && isNewModel;
  const canDisplayPopularBanner = displayPopularBanner && isPopular && viewers;
  const canDisplayMissMFCBanner = displayMissMFCBanner && isMissMFC && missMFCRank;

  //
  // Badges
  //
  const canDisplayFriendBadge = displayFriendBadge && isFriend;
  const canDisplayBookmarkBadge = displayBookmarkBadge && isBookmark;
  const canDisplayTrendingBadge = displayTrendingBadge && (!canEnablePinToTopSymbols || isPinnedToTopTrending) && isTrendingNew && trendingDiff;
  const canDisplayPopularBadge = displayPopularBadge && (!canEnablePinToTopSymbols || isPinnedToTopPopular) && isPopular && viewers;
  const canDisplayNewModelBadge = displayNewModelBadge && isNewModel;
  const canDisplayMissMFCBadge = displayMissMFCBadge && (!canEnablePinToTopSymbols || isPinnedToTopMissMFC || sort === 'miss_mfc') && isMissMFC && missMFCRank > 0;
  const canDisplayPriorMissMFCBadge = !!priorMissMfcRank;
  const canDisplayRecentlyVisitedBadge = displayRecentlyVisitedBadge && (!canEnablePinToTopSymbols || isPinnedToTopRecentlyVisited);

  //
  // Highlights
  //
  const canDisplayActiveRoomHighlight = activeRoom === id;
  const canDisplayFriendHighlight = displayFriendHighlight && isFriend;
  const canDisplayBookmarkHighlight = displayBookmarkHighlight && isBookmark;
  const canDisplayPopularHighlight = displayPopularHighlight && isPopular && (!canEnablePinToTopSymbols || isPinnedToTopPopular);
  const canDisplayTrendingHighlight = displayTrendingHighlight && isTrendingNew && (!canEnablePinToTopSymbols || isPinnedToTopTrending);
  const canDisplayRecentlyViewedHighlight = displayRecentlyVisitedHighlight && (!canEnablePinToTopSymbols || isPinnedToTopRecentlyVisited);
  const canDisplayRecommendationBadge = displayRecommendationBadge && (!canEnablePinToTopSymbols || isRecommended);
  const canDisplayRecommendationHighlight = displayRecommendationHighlight && isRecommended && (!canEnablePinToTopSymbols || isRecommended);
  const highlight = resolveDisplayHighlight({
    displayRecentlyVisitedHighlight: canDisplayRecentlyViewedHighlight,
    displayActiveRoomHighlight: canDisplayActiveRoomHighlight,
    displayTrendingHighlight: canDisplayTrendingHighlight,
    displayBookmarkHighlight: canDisplayBookmarkHighlight,
    displayPopularHighlight: canDisplayPopularHighlight,
    displayFriendHighlight: canDisplayFriendHighlight,
    displayRecommendationHighlight: canDisplayRecommendationHighlight
  });
  const canDisplayBanners = dicanDisplayGroupShowBanner || canDisplayTrendingBanner || canDisplayNewModelBanner || canDisplayMissMFCBanner || canDisplayPopularBanner;
  const canDisplayBadges = canDisplayPriorMissMFCBadge || canDisplayBookmarkBadge || canDisplayTrendingBadge || canDisplayNewModelBadge || canDisplayMissMFCBadge || canDisplayPopularBadge || canDisplayFriendBadge || canDisplayRecommendationBadge;

  // const modelColor = useModelColor(color)
  const hasBlurbOrTopic = !!blurb || !!topic;
  const hasLocationOrAge = !!country || !!age;
  const onPinCallback = useCallback((event: SyntheticEvent) => {
    event.stopPropagation();
    motionTransition(() => pinRoom(id, {
      pinned: !isRoomPinned
    }), {
      id,
      skipTransition: isRoomPinned,
      // skip if we're unpinning
      transitionSource: TransitionTypes.TILE,
      transitionDestination: TransitionTypes.PINROOM
    });
    // pinRoom(id, { pinned: !isRoomPinned })
  }, [id, isRoomPinned]);
  const onOpenMenuCallback = useCallback((event: SyntheticEvent) => {
    event.stopPropagation();
    openMenuDialog(slug, {
      hideNextPrev: disableMenuNextPrev,
      transitionSource: TransitionTypes.TILE
    });
  }, [slug, disableMenuNextPrev]);
  const onOpenContextMenuCallback = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (isAnonymous) return;
    onOpenMenuCallback(event);
    return false;
  }, [isAnonymous, onOpenMenuCallback]);
  const onGoToRoomCallback = useCallback((event: SyntheticEvent) => {
    event.stopPropagation();
    if (isAnonymous) return;
    if (loungeSlug) {
      // if we're in the lounge, set the surrogate
      setLoungeSurrogate(id, loungeSlug);
    } else {
      openRoom(slug, {
        transitionSource: TransitionTypes.TILE
      });
    }
  }, [isAnonymous, id, slug, loungeSlug]);
  const onOpenNote = useCallback((event: SyntheticEvent) => {
    event.stopPropagation();
    goToNotes(name);
  }, [id, name]);
  const shouldDisplayLargePreviewButton = canDisplayLargePreviewButton && (showVideoPreview || isTouchDevice || isPreviewPinned);
  const shouldDisplayPinButton = canDisplayPinButton && (showVideoPreview || isTouchDevice || isRoomPinned);
  const canShowMenu = !isAnonymous && (showVideoPreview || isTouchDevice);
  const canShowViewers = !!viewers && !canDisplayPopularBanner;
  return <div data-testid="Container__ModelDisplay" key={id} id={`tile-${id}`} style={highlight && {
    '--highlight': highlight
  } as CSSProperties} className={cx(stylesDetailedRoot, highlight && stylesDetailedRootHighlight)}>
            <div onClick={onGoToRoomCallback} onContextMenu={onOpenContextMenuCallback} data-testid="Container__Detailed" onMouseOver={isTouchDevice ? null : () => setVideoPreview(true)} onMouseLeave={isTouchDevice ? null : () => setVideoPreview(false)}
    // style={{
    //     height: displayHeight,
    // }}
    draggable onDragStart={dragStart} onDrag={dragMove} onDragEnd={dragEnd} className={detailedContainerStyles}>
                <VideoCover />
                <LiveSnapStream suppressVideo={passiveMode || !showVideoPreview && !shouldForceVideoPlay} isBroadcasting={isBroadcasting} compact={stylesMaxWidth < 200} videoID={VideoType.ModelTile} isTruePrivate={isTruePrivate} isPrivateShow={isPrivateShow} staticImage={staticImage} isGroupShow={isGroupShow} isAnonymous={isAnonymous} isClubShow={isClubShow} height={displayHeight} isOffline={isOffline} isNGVideo={isNGVideo} width={columnWidth} isLounge={isLounge} isModel={isModel} channel={channel} server={server} isAway={isAway} banned={isBanned} phase={phase} noBackground hideControls videoMuted id={id} />
                {(shouldDisplayLargePreviewButton || canDisplayNotesButton && !enableLargePreview || canDisplayRecentlyVisitedBadge) && <div className={cx(badgeContainerStyles, badgeCornerTopLeft)}>
                        {shouldDisplayLargePreviewButton && <LargeFloatingPreview size={stylesUtilities('iconSize')} id={id} />}
                        {canDisplayNotesButton && !enableLargePreview && <NotesButton size={stylesUtilities('iconSize')} filled title="Open Notes" onClick={onOpenNote} shadow />}
                        {canDisplayRecentlyVisitedBadge && <Container style={{
          '--margin': (shouldDisplayLargePreviewButton || canDisplayNotesButton && !enableLargePreview ? getStyle('roomIconOffset') || 8 : 8) + 'px'
        }} tooltipPlacement="bottom" title="Recently Visited" shadow>
                                <RoomIcon size={stylesUtilities('iconSize')} color="#fff" filled />
                            </Container>}
                    </div>}
                {(shouldDisplayPinButton || canDisplayNotesButton && enableLargePreview) && <div className={cx(badgeContainerStyles, badgeCornerTopRight)}>
                        {shouldDisplayPinButton && <PinButton title={(isRoomPinned ? 'Unpin' : 'Pin') + ' Model'} size={stylesUtilities('iconSize')} filled={isRoomPinned} onClick={onPinCallback} />}
                        {canDisplayNotesButton && enableLargePreview && <NotesButton size={stylesUtilities('iconSize')} filled tooltipPlacement={'bottom'} title="Open Notes" onClick={onOpenNote} shadow />}
                    </div>}
                {canDisplayBadges && <div style={{
        '--margin-bottom': stylesBadgeStatus('offsetY') + 'px',
        '--margin-left': stylesBadgeStatus('offsetX') + 'px'
      } as CSSProperties} className={cx(badgeContainerStyles, badgeCornerBottomLeft)}>
                        <div className={stylesDisplayBadges}>
                            {canDisplayFriendBadge && <FriendBadge size={stylesBadgeStatus('iconSize')} />}
                            {canDisplayBookmarkBadge && <BookmarkBadge size={stylesBadgeStatus('iconSize')} />}
                            {canDisplayRecommendationBadge && <RecommendationBadge size={stylesBadgeStatus('iconSize')} />}
                            {canDisplayPopularBadge && <PopularBadge size={stylesBadgeStatus('iconSize')} />}
                            {canDisplayTrendingBadge && <TrendingBadge size={stylesBadgeStatus('iconSize')} />}
                            {canDisplayPriorMissMFCBadge && <PriorMissMFCBadge className={stylesPriorMissMFCBadgeClass} priorMissMfcRank={priorMissMfcRank} style={{
            '--height': stylesPriorMissMFCBadge('height') + 'px',
            '--width': stylesPriorMissMFCBadge('width') + 'px'
          }} />}
                        </div>
                        {(canDisplayMissMFCBadge || canDisplayNewModelBadge) && <NewAndMissMFCBadges className={stylesNewAndMissMFCBadges} style={{
          '--font-size': stylesBadgeNewMissMFC('fontSize') + 'px'
        }} showMissMFC={canDisplayMissMFCBadge} showNew={canDisplayNewModelBadge} missMFCRank={missMFCRank} />}
                    </div>}
                {(canShowViewers || canShowMenu) && <div style={{
        '--margin-bottom': stylesBadgeStatus('offsetY') + 'px',
        '--margin-right': stylesBadgeStatus('offsetX') + 'px'
      } as CSSProperties} className={cx(badgeContainerStyles, badgeCornerBottomRight)}>
                        {canShowViewers && <div className={stylesViewerContainer}>
                                <IconViewers title={locales.modelTile.viewers} size={stylesViewers('iconSize')} color="#fff" styles={{
            '--margin-bottom': stylesViewers('offsetY') + 'px'
          } as CSSProperties} />
                                <span style={{
            '--font-size': stylesViewers('fontSize') + 'px'
          } as CSSProperties} className={stylesViewerCounter}>
                                    {viewers}
                                </span>
                            </div>}
                        {canShowMenu && <ButtonModelMenu onClick={onOpenMenuCallback} />}
                    </div>}
            </div>
            <div onClick={onGoToRoomCallback} onContextMenu={onOpenContextMenuCallback} className={bannerContainerStyles}>
                {canDisplayBanners && <>
                        {canDisplayMissMFCBanner && <MissMFCBanner className={stylesHeightAndFontSizeAndPaddingDefault} style={{
          '--font-size': stylesBanner('fontSize') + 'px',
          '--height': stylesBanner('height') + 'px'
        }} rankClassName={stylesMargin} rankStyle={{
          '--margin': stylesBannerMissMFCRank('margin')
        }} text={stylesBannerMissMFC('text')} missMFCPrize={missMFCPrize} missMFCRank={missMFCRank} />}
                        {canDisplayTrendingBanner && <TrendingBanner className={stylesHeightAndFontSize} style={{
          '--font-size': stylesBanner('fontSize') + 'px',
          '--height': stylesBanner('height') + 'px'
        }} diffClassName={stylesMargin} diffStyle={{
          '--margin': stylesBannerTrendingDiff('margin')
        }} text={stylesBannerTrending('text')} diff={trendingDiff} />}
                        {canDisplayPopularBanner && <PopularBanner className={stylesHeightAndFontSize} style={{
          '--font-size': stylesBanner('fontSize') + 'px',
          '--height': stylesBanner('height') + 'px'
        }} viewersClassName={stylesMargin} viewersStyle={{
          '--margin': stylesBannerPopularViewers('margin')
        }} iconSize={stylesBanner('iconSize')} text={stylesBannerPopular('text')} viewers={viewers} />}
                        {dicanDisplayGroupShowBanner && (isGroupShow || isGroupShowPending || groupShowRoomCount) && <GroupShowBanner className={stylesHeightAndFontSizeAndPaddingDefault} style={{
          '--font-size': stylesBanner('fontSize') + 'px',
          '--height': stylesBanner('height') + 'px'
        }} iconSize={stylesBanner('iconSize')} roomCount={groupShowRoomCount} isLive={isGroupShow} />}
                        {canDisplayNewModelBanner && <NewModelBanner className={stylesHeightAndFontSize} style={{
          '--font-size': stylesBanner('fontSize') + 'px',
          '--height': stylesBanner('height') + 'px'
        }} timeClassName={stylesMargin} timeStyle={{
          '--margin': stylesBannerNewTime('margin')
        }} creation={creation} />}
                        <div style={{
          // ${
          //     hexRGBA(modelColor, 0.3) ||
          //     'rgba(0,0,0, 0.3)'
          // }`
          '--height': stylesBanner('divider') + 'px'
        } as CSSProperties} className={cx(stylesDivider, stylesDividerBorder)} />
                    </>}
                <ModelName className={cx(stylesModelName, stylesHeightAndFontSize, !canDisplayBanners && stylesModelNamePaddinTop)} style={{
        '--font-size': stylesName('fontSize')() + 'px',
        '--height': stylesName('height') + 'px'
      }} isAnonymous={isAnonymous} color={color} modelId={id} name={name} />
                <div style={{
        // borderTop: `1px solid rgba(0,0,0,.12)`,
        // ${
        //     hexRGBA(modelColor, 0.3) || 'rgba(0,0,0, 0.3)'
        // }`,
        '--height': stylesName('divider') + 'px'
      } as CSSProperties} className={stylesDivider} />
                <div className={stylesBadgeAvatarContainer} style={{
        // TODO (Marc): Already has `px` in it and should
        // probably  be broken down to match the pattern of
        // everything else. But otherwise, this is working
        // fine...
        '--padding': stylesDescription('padding'),
        '--margin': stylesDescriptionAvatar('margin')
      } as CSSProperties}>
                    <BadgeAvatar avatarClassName={stylesBadgeAvatar} size={stylesDescription('size')} isBroadcasting={isBroadcasting} onClick={onOpenMenuCallback} isOffline={isOffline} disableBadge alt={name} id={id} className={stylesDescriptionAvatarClass} />
                    <div id={`blurb-${id}`} className={blurbContainerStyles} style={{
          '--line-height': stylesDescription('lineHeight') + 'px',
          '--line-clamp': stylesDescription('lineCount'),
          '--font-size': stylesDescription('fontSize') + 'px',
          '--height': stylesDescription('height')() + 'px'
        } as CSSProperties}>
                        {hasBlurbOrTopic && <span className={cx(stylesBlurbOrTopic, 'mfc-age-gate')}>
                                {!!blurb && blurb}
                                {!!blurb && !!topic && ' — '}
                                {!!topic && topic}
                            </span>}
                        {!hasBlurbOrTopic && <span className={cx('mfc-age-gate', blurbStyles)}>
                                {isAnonymous ? 'Bio is anonymous.' : 'No bio yet...'}
                            </span>}
                    </div>
                </div>
                {hasLocationOrAge && <>
                        <div className={stylesDivider} style={{
          // borderBottom: `1px solid ${
          //     hexRGBA(modelColor, 0.3) ||
          //     'rgba(0,0,0, 0.3)'
          // }`,
          '--height': stylesInfo('divider') + 'px'
        } as CSSProperties} />
                        <div className={cx(locationAgeContainer, stylesHeightAndFontSize)} style={{
          '--font-size': stylesInfo('fontSize') + 'px',
          '--height': stylesInfo('height') + 'px'
        } as CSSProperties}>
                            {!!country && <>
                                    <MapMarker size={0.5} />
                                    <span className={countryStyles}>
                                        {country}
                                    </span>
                                </>}
                            {!!age && <>
                                    <Calendar size={0.5} />
                                    <span className={calendarStyles}>
                                        {age || '18'}
                                    </span>
                                </>}
                        </div>
                    </>}
            </div>
        </div>;
});
export default Detailed;